/**
 * Permission-based authorization for Wellspring application.
 *
 * This module defines the available permissions and provides functions to check
 * if a user has a specific permission based on their group membership.
 *
 * ## Role Definitions
 *
 * ### SuperAdmin (Wellspring-SuperAdmin)
 * Everything Admin has, plus meta-administration of Wellspring itself
 * (defined as ADMIN_PERMISSIONS + MANAGE_WELLSPRING_USERS):
 * - Manage users in Wellspring's own Cognito pool
 *
 * ### Admin (Wellspring-Admin)
 * Full access to all features including:
 * - User management (create, update, delete, reset passwords)
 * - Customer management (view, assign to users)
 * - Customer onboarding (create entries, view queue)
 * - DataLink integration (onboard customers, view accounts)
 * - Metrics (view, refresh)
 * - Infrastructure spend (dashboard Spend tab)
 * - Audit logs (view, search, export)
 * - Property uploads (view all uploads from all users)
 * - Dashboard (full access)
 *
 * ### Viewer (Wellspring-Viewer)
 * Read-only access (defined in VIEWER_PERMISSIONS):
 * - View dashboard
 * - View metrics
 * - View customers
 * - View meter inspector
 * - No user management
 * - Cannot refresh metrics
 *
 * ### Support (Wellspring-Support)
 * Most of Admin, minus destructive and meta-admin operations (no user deletes,
 * audit logs, feature flags, DataLink onboarding, or system status):
 * - User management (create, update, reset passwords)
 * - Customer management (view, assign to users)
 * - Customer onboarding (create entries, view queue)
 * - Metrics (view, refresh)
 * - Dashboard (full access)
 *
 * ### metron-internal (auto-assigned by email domain)
 * Always mirrors Viewer permissions (shared via VIEWER_PERMISSIONS constant):
 * - View dashboard
 * - View metrics
 * - View customers
 * - View meter inspector
 *
 * Users with Metron email domains are automatically added to the
 * metron-internal Cognito group, granting them Viewer-level access
 * without requiring an explicit Wellspring-Viewer group assignment.
 */

/**
 * User groups (array of role strings without 'Wellspring-' prefix)
 */
export type UserGroups = string[];

/**
 * Wellspring role constants.
 * Note: The Lambda authorizer strips the 'Wellspring-' prefix from group names.
 */
export const WellspringRole = {
  SUPER_ADMIN: 'SuperAdmin',
  ADMIN: 'Admin',
  VIEWER: 'Viewer',
  SUPPORT: 'Support',
} as const;

/**
 * Type for Wellspring roles
 */
export type WellspringRole = typeof WellspringRole[keyof typeof WellspringRole];

/**
 * FlowControl role constants.
 * These map to Cognito groups for the FlowControl feature flag system.
 */
export const FlowControlRole = {
  ADMIN: 'FlowControl-Admin',
  EDITOR: 'FlowControl-Editor',
  VIEWER: 'FlowControl-Viewer',
} as const;

/**
 * Type for FlowControl roles
 */
export type FlowControlRole = typeof FlowControlRole[keyof typeof FlowControlRole];

/**
 * Internal group constants.
 * These map to Cognito groups assigned automatically based on email domain.
 */
export const InternalRole = {
  METRON_INTERNAL: 'metron-internal',
} as const;

/**
 * Type for internal roles
 */
export type InternalRole = typeof InternalRole[keyof typeof InternalRole];

/**
 * Union of all application roles
 */
export type AppRole = WellspringRole | FlowControlRole | InternalRole;

/**
 * Available permissions in the system
 */
export enum Permission {
  // Dashboard & Metrics
  VIEW_DASHBOARD = 'view:dashboard',
  VIEW_METRICS = 'view:metrics',
  REFRESH_METRICS = 'refresh:metrics',
  // Dollars on the dashboard (the Spend and Revenue tabs). Admin-tier only:
  // Viewer, Support and metron-internal get Home and Users but not infra
  // spend or company revenue.
  VIEW_SPEND_METRICS = 'view:spend_metrics',
  // Per-customer revenue (Epicor-attributed) on the customers list and detail
  // page. Admin-tier only, same audience as VIEW_SPEND_METRICS: the money
  // never leaves the server for Viewer, Support or metron-internal.
  VIEW_REVENUE = 'view:revenue',

  // User Management
  VIEW_USERS = 'view:users',
  CREATE_USER = 'create:user',
  UPDATE_USER = 'update:user',
  DELETE_USER = 'delete:user',
  RESET_PASSWORD = 'reset:password',
  REGENERATE_TEMP_PASSWORD = 'users:regenerate-temp-password',

  // Wellspring Meta-Administration
  // Users in Wellspring's own Cognito pool, as opposed to the Plus pool that
  // VIEW_USERS/CREATE_USER etc. govern. SuperAdmin only.
  MANAGE_WELLSPRING_USERS = 'manage:wellspring-users',
  // Assign Wellspring-* roles below your own (see wellspring-roles.ts). Admin and up.
  MANAGE_WELLSPRING_ROLES = 'manage:wellspring-roles',

  // Customer Management
  VIEW_CUSTOMERS = 'view:customers',
  MANAGE_CUSTOMER_ASSIGNMENTS = 'manage:customer_assignments',

  // Customer Onboarding
  VIEW_ONBOARDING_QUEUE = 'view:onboarding_queue',
  CREATE_ONBOARDING_ENTRY = 'create:onboarding_entry',

  // DataLink Integration
  VIEW_DATALINK_ACCOUNTS = 'view:datalink_accounts',
  ONBOARD_DATALINK_CUSTOMER = 'onboard:datalink_customer',
  MANAGE_DATALINK_ACCOUNTS = 'manage:datalink_accounts',

  // Audit Logs
  VIEW_AUDIT_LOGS = 'view:audit_logs',

  // Property Uploads
  VIEW_ALL_PROPERTY_UPLOADS = 'view:all_property_uploads',

  // Property Management
  EDIT_PROPERTIES = 'edit:properties',
  DELETE_PROPERTIES = 'delete:properties',

  // End Consumers
  MANAGE_END_CONSUMERS = 'manage:end_consumers',

  // Feature Flags
  VIEW_FEATURE_FLAGS = 'view:feature_flags',
  MANAGE_FEATURE_FLAGS = 'manage:feature_flags',

  // Meter Inspector
  VIEW_METER_INSPECTOR = 'view:meter_debugger',

  // Custom Group Management
  VIEW_CUSTOM_GROUPS = 'view:custom_groups',
  EDIT_CUSTOM_GROUPS = 'edit:custom_groups',
  DELETE_CUSTOM_GROUPS = 'delete:custom_groups',

  // System Diagnostics
  // Gates /system/status, which exposes DB host/user/port/name to the caller
  // (review 2026-05-10, finding H4). Admin-only.
  VIEW_SYSTEM_STATUS = 'view:system_status',
}

/**
 * Viewer permissions — shared between Wellspring-Viewer and metron-internal roles.
 * metron-internal always mirrors Viewer; update this single list for both.
 */
const VIEWER_PERMISSIONS: Permission[] = [
  Permission.VIEW_DASHBOARD,
  Permission.VIEW_METRICS,
  Permission.VIEW_CUSTOMERS,
  Permission.VIEW_METER_INSPECTOR,
];

/**
 * Admin permissions — everything except Wellspring meta-administration.
 * SuperAdmin is this list plus MANAGE_WELLSPRING_USERS.
 */
const ADMIN_PERMISSIONS: Permission[] = [
  Permission.VIEW_DASHBOARD,
  Permission.VIEW_METRICS,
  Permission.REFRESH_METRICS,
  Permission.VIEW_SPEND_METRICS,
  Permission.VIEW_REVENUE,
  Permission.VIEW_USERS,
  Permission.CREATE_USER,
  Permission.UPDATE_USER,
  Permission.DELETE_USER,
  Permission.RESET_PASSWORD,
  Permission.REGENERATE_TEMP_PASSWORD,
  Permission.MANAGE_WELLSPRING_ROLES,
  Permission.VIEW_CUSTOMERS,
  Permission.MANAGE_CUSTOMER_ASSIGNMENTS,
  Permission.VIEW_ONBOARDING_QUEUE,
  Permission.CREATE_ONBOARDING_ENTRY,
  Permission.VIEW_DATALINK_ACCOUNTS,
  Permission.ONBOARD_DATALINK_CUSTOMER,
  Permission.MANAGE_DATALINK_ACCOUNTS,
  Permission.VIEW_AUDIT_LOGS,
  Permission.VIEW_ALL_PROPERTY_UPLOADS,
  Permission.EDIT_PROPERTIES,
  Permission.DELETE_PROPERTIES,
  Permission.VIEW_FEATURE_FLAGS,
  Permission.MANAGE_FEATURE_FLAGS,
  Permission.VIEW_METER_INSPECTOR,
  Permission.VIEW_CUSTOM_GROUPS,
  Permission.EDIT_CUSTOM_GROUPS,
  Permission.DELETE_CUSTOM_GROUPS,
  Permission.MANAGE_END_CONSUMERS,
  Permission.VIEW_SYSTEM_STATUS,
];

/**
 * Role to permissions mapping
 */
const ROLE_PERMISSIONS: Record<AppRole, Permission[]> = {
  [WellspringRole.SUPER_ADMIN]: [
    ...ADMIN_PERMISSIONS,
    Permission.MANAGE_WELLSPRING_USERS,
  ],
  [WellspringRole.ADMIN]: ADMIN_PERMISSIONS,
  [WellspringRole.VIEWER]: VIEWER_PERMISSIONS,
  [WellspringRole.SUPPORT]: [
    // Support has most of Admin but deliberately excludes destructive and
    // meta-admin operations. It lacks: VIEW_SPEND_METRICS, VIEW_REVENUE, DELETE_USER,
    // ONBOARD_DATALINK_CUSTOMER, MANAGE_DATALINK_ACCOUNTS, VIEW_AUDIT_LOGS,
    // VIEW_ALL_PROPERTY_UPLOADS, VIEW_FEATURE_FLAGS, MANAGE_FEATURE_FLAGS,
    // DELETE_CUSTOM_GROUPS, VIEW_SYSTEM_STATUS. This list is intentionally
    // spelled out rather than derived from ADMIN_PERMISSIONS — additions to
    // Admin must be granted to Support explicitly, not inherited by accident.
    Permission.VIEW_DASHBOARD,
    Permission.VIEW_METRICS,
    Permission.REFRESH_METRICS,
    Permission.VIEW_USERS,
    Permission.CREATE_USER,
    Permission.UPDATE_USER,
    Permission.RESET_PASSWORD,
    Permission.REGENERATE_TEMP_PASSWORD,
    Permission.VIEW_CUSTOMERS,
    Permission.MANAGE_CUSTOMER_ASSIGNMENTS,
    Permission.VIEW_ONBOARDING_QUEUE,
    Permission.CREATE_ONBOARDING_ENTRY,
    Permission.VIEW_DATALINK_ACCOUNTS,
    Permission.EDIT_PROPERTIES,
    Permission.DELETE_PROPERTIES,
    Permission.VIEW_METER_INSPECTOR,
    Permission.VIEW_CUSTOM_GROUPS,
    Permission.EDIT_CUSTOM_GROUPS,
    Permission.MANAGE_END_CONSUMERS,
  ],
  [FlowControlRole.ADMIN]: [
    Permission.VIEW_FEATURE_FLAGS,
    Permission.MANAGE_FEATURE_FLAGS,
  ],
  [FlowControlRole.EDITOR]: [
    Permission.VIEW_FEATURE_FLAGS,
    Permission.MANAGE_FEATURE_FLAGS,
  ],
  [FlowControlRole.VIEWER]: [
    Permission.VIEW_FEATURE_FLAGS,
  ],
  // metron-internal users (auto-assigned by email domain) get Viewer-equivalent access
  [InternalRole.METRON_INTERNAL]: VIEWER_PERMISSIONS,
};

/**
 * Check if a user has a specific permission based on their groups.
 *
 * @param userGroups - Array of group names the user belongs to (without 'Wellspring-' prefix)
 * @param permission - The permission to check
 * @returns true if the user has the permission, false otherwise
 *
 * @example
 * ```ts
 * const userGroups = ['Admin'];
 * if (checkPermission(userGroups, Permission.CREATE_USER)) {
 *   // User can create users
 * }
 * ```
 */
export function checkPermission(
  userGroups: UserGroups,
  permission: Permission,
): boolean {
  // Check if any of the user's groups grants the requested permission
  for (const group of userGroups) {
    const role = group as AppRole;
    const permissions = ROLE_PERMISSIONS[role];
    if (permissions && permissions.includes(permission)) {
      return true;
    }
  }
  return false;
}

/**
 * Check if a user has ANY of the specified permissions.
 *
 * @param userGroups - Array of group names the user belongs to
 * @param permissions - Array of permissions to check
 * @returns true if the user has at least one of the permissions
 */
export function checkAnyPermission(
  userGroups: UserGroups,
  permissions: Permission[],
): boolean {
  return permissions.some((permission) => checkPermission(userGroups, permission));
}

/**
 * Check if a user has ALL of the specified permissions.
 *
 * @param userGroups - Array of group names the user belongs to
 * @param permissions - Array of permissions to check
 * @returns true if the user has all of the permissions
 */
export function checkAllPermissions(
  userGroups: UserGroups,
  permissions: Permission[],
): boolean {
  return permissions.every((permission) => checkPermission(userGroups, permission));
}

/**
 * Check if a user belongs to a specific Wellspring role.
 *
 * @param userGroups - Array of group names the user belongs to
 * @param role - The role to check
 * @returns true if the user belongs to the role
 */
export function checkRole(userGroups: UserGroups, role: WellspringRole): boolean {
  return userGroups.includes(role);
}

/**
 * Roles that carry Admin-tier access. SuperAdmin sits above Admin, so every
 * "is this user an admin?" check must accept it too.
 *
 * Anything gating on role *identity* rather than a Permission should go
 * through hasAdminAccess() and add new elevated roles here — checking
 * `groups.includes('Admin')` directly is what locked SuperAdmin out of the
 * admin routes and the grantable-roles list when it was introduced.
 */
const ADMIN_TIER_ROLES: WellspringRole[] = [
  WellspringRole.ADMIN,
  WellspringRole.SUPER_ADMIN,
];

/**
 * Check if a user has Admin-tier access (Admin or any role above it).
 *
 * Prefer a specific Permission where one exists; this is for the gates that
 * genuinely mean "an administrator", such as the /admin route guard.
 *
 * @param userGroups - Array of group names the user belongs to
 * @returns true if the user holds an Admin-tier role
 */
export function hasAdminAccess(userGroups: UserGroups): boolean {
  return ADMIN_TIER_ROLES.some((role) => userGroups.includes(role));
}
